import React, { Fragment, useEffect, useState } from 'react';
import Button from '../../common/Button';
import ReactPaginate from 'react-paginate';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import {
  cancelSubscription,
  downloadInvoice,
  getCurrentSubscription
} from '../../api/subscription';
import ToastNotification from '../../common/ToastNotification';
import { useNavigate } from 'react-router-dom';
import downloadgif from '../../assets/gif/Download.gif';
import { formatDateAndTime } from '../../function/Helper';
import { getSubscriptionDetail } from '../../common/CommonFunction';
import {
  cancelRetailerSubscription,
  getRetailerCurrentSubscription
} from '../../api/RetailerSubscription';

const SubscriptionDetail = () => {
  const navigate = useNavigate();
  const shopId = sessionStorage.getItem('shopId');
  const businessType = sessionStorage.getItem('businessType');

  const itemsPerPage = 4;
  const [currentPage, setCurrentPage] = useState(0);
  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionDetail, setSubscriptionDetail] = useState<any>();
  const [invoiceList, setInvoiceList] = useState([]);
  const [disable, setDisable] = useState(false);

  const getPlanDetail = async () => {
    try {
      setDataIsLoading(true);
      const response =
        businessType === 'Retail'
          ? await getRetailerCurrentSubscription(shopId)
          : await getCurrentSubscription();
      if (response) {
        setSubscriptionDetail(response?.payload);
        setInvoiceList(response?.payload?.invoices);
      }
      setDataIsLoading(false);
    } catch (error: any) {
      setDataIsLoading(false);
    }
  };

  useEffect(() => {
    getPlanDetail();
  }, []);

  const offset = currentPage * itemsPerPage;
  const invoiceData = invoiceList?.slice(offset, offset + itemsPerPage);
  const totalPages = Math.ceil(invoiceList?.length / itemsPerPage);

  const getDaysLeft = (currentPeriodEnd: string) => {
    const today: any = new Date();

    // Parse the currentPeriodEnd date
    const endDate: any = new Date(currentPeriodEnd);

    // Calculate the difference in time (in milliseconds)
    const timeDiff = endDate - today;

    // Convert the time difference into days
    const days = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return days;
  };

  const handlePageClick = (selectedItem: any) => {
    setCurrentPage(selectedItem.selected);
  };

  const handleCancelSubscription = async () => {
    setDisable(true);
    const response =
      businessType === 'Retail'
        ? await cancelRetailerSubscription(shopId)
        : await cancelSubscription();
    if (response.ok) {
      const data = await response.json();
      if (data) {
        ToastNotification({
          message: 'Subscription canceled successfully'
        });
        getSubscriptionDetail(businessType, shopId);
        getPlanDetail();
        setDisable(false);
      }
    }
  };

  const getFormattedDate = (date: string) => {
    const formattedDate = new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return formattedDate;
  };

  const downloadInvoiceHandler = async (id: any) => {
    setIsLoading(true);
    await downloadInvoice(id)
      .then((res: any) => {
        if (!res) {
          ToastNotification({
            message: 'Failed to download',
            type: 'error'
          });
          setIsLoading(false);
          return;
        }

        const byteArray = res?.payload?.pdf;
        const padfContent = new Uint8Array(byteArray);
        const blob = new Blob([padfContent], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = res?.payload?.filename;
        link.click();
        URL.revokeObjectURL(url);

        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  return (
    <>
      {dataIsLoading ? (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>

          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="loader border-t-4 border-[#006BEA] rounded-full w-12 h-12 animate-spin"></div>
          </div>
        </>
      ) : (
        subscriptionDetail && (
          <div className="p-4">
            <div className="text-sm md:text-base font-medium">Current Plan</div>
            <div className="bg-[#343434] p-5 rounded-[10px] mt-6">
              <div className="text-sm md:text-base font-medium first-letter:capitalize">
                {subscriptionDetail.status !== 'cancelled' ? (
                  <span>
                    {subscriptionDetail.plan &&
                    subscriptionDetail.plan.data.analytics === 'Free' ? (
                      <span>Free Plan</span>
                    ) : (
                      <span>
                        {subscriptionDetail && subscriptionDetail.billingCycle
                          ? subscriptionDetail.billingCycle
                          : ''}
                      </span>
                    )}
                  </span>
                ) : (
                  <span>Subscription Canceled</span>
                )}
              </div>
              <hr className="border-[#595959] border my-4 rounded" />

              {subscriptionDetail.status !== 'cancelled' ? (
                <Fragment>
                  <div className="text-xs md:text-sm mb-2">
                    The subscription renewal date for your service is approaching; please ensure it
                    is updated to avoid any interruptions.
                  </div>
                  <div className="text-sm md:text-base font-medium mb-5">
                    {subscriptionDetail.plan &&
                    subscriptionDetail.plan.data.analytics === 'Free' ? (
                      <span>N/A</span>
                    ) : (
                      <span>
                        {subscriptionDetail && subscriptionDetail.currentPeriodEnd
                          ? formatDateAndTime(subscriptionDetail.currentPeriodEnd)
                          : ''}
                        <span className="ml-1">
                          (in {getDaysLeft(subscriptionDetail?.currentPeriodEnd)} days)
                        </span>
                      </span>
                    )}
                  </div>
                  {subscriptionDetail.plan && subscriptionDetail.plan.data.analytics !== 'Free' && (
                    <div className="text-xs md:text-sm mb-2">
                      You will be charged the applicable amount based on your subscription plan at
                      the time of renewal.
                    </div>
                  )}

                  {subscriptionDetail.selectedPlanPricing && (
                    <div className="text-sm md:text-base font-medium mb-5">
                      {subscriptionDetail.selectedPlanPricing.currency === 'USD' ? '$' : 'Rs'}
                      <span className="ml-1">
                        {subscriptionDetail.billingCycle === 'monthly'
                          ? subscriptionDetail.selectedPlanPricing['monthlyPrice']
                          : subscriptionDetail.selectedPlanPricing['yearlyPrice']}
                      </span>
                    </div>
                  )}

                  <div className="mt-6">
                    <label className="text-sm md:text-base font-medium">
                      Explore your {subscriptionDetail.planDetail.name.toLowerCase()} plan benefits
                    </label>
                    <hr className="border-[#595959] border my-4 rounded" />
                    <div className="flex flex-col gap-2">
                      {subscriptionDetail.planDetail.features[0].map((info: any, index: number) => (
                        <div key={index} className="flex flex-row gap-1.5 text-xs md:text-sm">
                          <label>▪</label>
                          <label>{info}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </Fragment>
              ) : (
                <Fragment>
                  <div className="text-xs md:text-sm mb-2">
                    Your Subscription was canceled. You can subscribe any time when you required.
                  </div>
                  <div className="text-sm md:text-base font-medium mb-5">
                    Canceled on
                    <span className="ml-2">
                      {subscriptionDetail && subscriptionDetail.updatedAt
                        ? formatDateAndTime(subscriptionDetail.updatedAt)
                        : ''}
                    </span>
                  </div>
                </Fragment>
              )}
            </div>

            {subscriptionDetail.plan.data.analytics !== 'Free' && (
              <div className="mt-3 text-sm md:text-base font-medium">
                There is 14 days of Free trial you can use and then automatically you will be
                Switched to your plan
              </div>
            )}

            {subscriptionDetail.status !== 'cancelled' ? (
              <div className="flex gap-8 items-center">
                {subscriptionDetail.plan &&
                  subscriptionDetail.plan.data.analytics !== 'Advanced' && (
                    <Button
                      isFormValid={true}
                      btnText="Upgrade Plan"
                      textColor="#FFFFFF"
                      backGroundColor="#006BEA"
                      borderColor=""
                      onClick={() => {
                        sessionStorage.setItem('redirectFrom', window.location.pathname);
                        navigate(
                          `/subscription?recentPlanType=${subscriptionDetail.plan.data.analytics}&updatePlan=${true}`
                        );
                      }}
                    />
                  )}

                {subscriptionDetail.plan && subscriptionDetail.plan.data.analytics !== 'Free' && (
                  <span
                    className={`text-xs md:text-sm mt-4 mb-2 ${disable ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => (disable ? {} : handleCancelSubscription())}
                  >
                    <u>Cancel Subscription</u>
                  </span>
                )}
              </div>
            ) : (
              <div className="flex gap-8 items-center">
                <Button
                  isFormValid={true}
                  btnText="Subscribe"
                  textColor="#FFFFFF"
                  backGroundColor="#006BEA"
                  borderColor=""
                  onClick={() =>
                    navigate(
                      `/subscription?recentPlanType=${subscriptionDetail.plan.data.analytics}&updatePlan=${true}`
                    )
                  }
                />
              </div>
            )}

            {subscriptionDetail.status !== 'cancelled' && businessType !== 'Retail' && (
              <Fragment>
                <div className="text-sm md:text-base font-medium">Tax Invoices</div>

                {invoiceData?.length > 0 ? (
                  <Fragment>
                    <div className="overflow-y-auto pr-2">
                      <table className="overflow-x-auto table-users  text-white border-separate border-spacing-y-3">
                        <thead className="">
                          <tr>
                            <th className="pl-3 md:py-2 text-left md:text-[14px] sm:text-[6px]">
                              Date
                            </th>
                            <th className="md:py-2 text-left md:text-[14px] sm:text-[6px]">
                              TAX INVOICES
                            </th>
                            <th className="md:py-2 text-left md:text-[14px] sm:text-[6px] pr-1">
                              TOTAL AMOUNTS
                            </th>
                            <th className="md:py-2 text-left md:text-[14px] sm:text-[6px] pr-1"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceData.map((item: any, index: number) => (
                            <tr key={index} className="bg-[#343434]">
                              <td className="pl-3 py-2 md:text-[14px] sm:text-[6px] rounded-tl-md rounded-bl-md">
                                {formatDateAndTime(item?.paidAt)}
                              </td>
                              <td className="pl-1 py-2 md:text-[14px] sm:text-[6px]">
                                <div className="w-30 text-ellipsis overflow-hidden whitespace-nowrap">
                                  {item?.razorpayInvoiceId}
                                </div>
                              </td>
                              <td className={`py-2 text-[14px]`}>
                                {item?.currency === 'INR' ? '₹' : '$'}
                                {item?.amount}
                              </td>
                              <td className="pl-1 py-2 md:text-[14px] sm:text-[6px] rounded-tr-md rounded-br-md">
                                {' '}
                                <div className="flex">
                                  {isLoading ? (
                                    // <pre>{JSON.stringify(downloadLoader, null, 2)}</pre>
                                    // <Lottie options={defaultOptions} height={20} width={20} />
                                    <img src={downloadgif} alt="" height={20} width={20} />
                                  ) : (
                                    <FiDownload
                                      className="text-[#006BEA] w-5 h-5 cursor-pointer"
                                      onClick={() =>
                                        downloadInvoiceHandler(item?.razorpayInvoiceId)
                                      }
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {invoiceData?.length >= 1 && (
                        <div className="">
                          <div className="w-full flex justify-center items-center mt-4 mb-3">
                            <ReactPaginate
                              previousLabel={
                                <FaChevronLeft className="flex items-center justify-center w-6 h-6 p-1 border rounded border-white text-white" />
                              }
                              nextLabel={
                                <FaChevronRight className="flex items-center justify-center w-6 h-6 p-1 border rounded border-white text-white" />
                              }
                              forcePage={currentPage}
                              pageCount={totalPages}
                              onPageChange={handlePageClick}
                              containerClassName="flex items-center justify-center gap-3"
                              activeClassName="text-[#006BEA] font-bold"
                              disabledClassName="opacity-50 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <br />
                  </Fragment>
                ) : (
                  <div className="flex items-center justify-center h-[40vh] text-[16px] text-gray-300 select-none">
                    No records found
                  </div>
                )}
              </Fragment>
            )}
          </div>
        )
      )}
    </>
  );
};

export default SubscriptionDetail;
